/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Chakra Components
import {
    Box,
    Flex,
    Stack,
    VisuallyHidden,
    useMultiStyleConfig,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import ItemVariantProvider from '../item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemBrand from '../item-variant/item-brand'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import QuantityPicker from '../../components/quantity-picker'
import DisplayPrice from '../display-price'
import {FewItemsLeftMessage} from '../../components/few-items-left'
import {getPriceData} from '../../utils/product-utils'
import {usePromotions} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

// Utilities
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import {useCurrency, useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const ProductItem = (
    {
        product,
        primaryAction,
        onItemQuantityChange = noop,
        showLoading = false,
        removeAction,
        editAction,
        wishlistAction,
        isMiniCart,
        variant,
        isFewItemsLeft,
        isLastItem
    },
    ref
) => {
    const {stepQuantity, showInventoryMessage, quantity, setQuantity} = useDerivedProduct(product)
    const {currency: activeCurrency} = useCurrency()
    const intl = useIntl()
    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const styles = useMultiStyleConfig('ProductItem', {variant})

    const isGreyedOut = !product?.inventory?.orderable

    const priceData = useMemo(() => {
        // Check if product has only approaching discount promotion
        const hasOnlyApproachingPromotion =
            product?.productPromotions?.length === 1 &&
            product.productPromotions[0]?.promotionId?.includes('approaching-discount')

        // If product has basket pricing (itemId exists), use basket prices instead of recalculating
        if (product?.itemId) {
            // Calculate current price per unit considering discounts
            let currentPrice = product.basePrice

            // If there are price adjustments, use priceAfterItemDiscount to get the actual discounted price
            if (
                product?.priceAdjustments &&
                product.priceAdjustments.length > 0 &&
                product.priceAfterItemDiscount
            ) {
                // Calculate the discounted price per unit
                currentPrice = product.priceAfterItemDiscount / (product.quantity || 1)
            }

            // Get list price: prioritize c_listPrice from backend, then tieredPrices, then product.price
            const listPriceTier = product?.tieredPrices?.find((tier) =>
                tier.pricebook?.includes('list_price')
            )
            const listPrice = product?.c_listPrice || listPriceTier?.price || product.price

            return {
                currentPrice: currentPrice,
                listPrice: listPrice,
                pricePerUnit: currentPrice,
                isASet: product?.type?.set,
                isMaster: product?.type?.master,
                isRange: product?.type?.set || product?.type?.master || false,
                isOnSale: listPrice > currentPrice,
                hasPromotion: product?.productPromotions && product?.productPromotions?.length > 0,
                hasOnlyApproachingPromotion,
                newArrivalDate: product?.c_newArrivalDate
            }
        }
        // Otherwise use the standard price calculation
        const standardPriceData = getPriceData(product, {quantity})
        return {
            ...standardPriceData,
            hasOnlyApproachingPromotion
        }
    }, [product, quantity])

    const promotionId = useMemo(() => {
        return product?.productPromotions?.[0]?.promotionId
    }, [product?.productPromotions])

    const promotionsQuery = usePromotions(
        {
            parameters: {ids: promotionId}
        },
        {
            enabled: !!promotionId
        }
    )

    const ENABLE_FINAL_SALE_LABEL = getSiteEnvVarValue(config.app, 'ENABLE_FINAL_SALE_LABEL', siteId)
    let enableFinalSaleLabel =
        ENABLE_FINAL_SALE_LABEL === true ||
        ENABLE_FINAL_SALE_LABEL === 'true' ||
        product?.c_isEGiftCard ||
        product?.c_isGiftCard

    const promotionData = promotionsQuery?.data?.data?.[0] || {}

    const renderPromotion = () => {
        // Only show promotion if there's actual evidence it's applied:
        // 1. Price discount (priceAfterItemDiscount/priceAfterOrderDiscount < price)
        // 2. Price adjustments exist
        // 3. Other indicators of applied promotions
        const hasPriceDiscount =
            (product?.priceAfterItemDiscount && product.priceAfterItemDiscount < product?.price) ||
            (product?.priceAfterOrderDiscount && product.priceAfterOrderDiscount < product?.price)

        const hasPriceAdjustments = product?.priceAdjustments?.length > 0

        const isPromotionApplied = hasPriceDiscount || hasPriceAdjustments

        if (!isPromotionApplied || !promotionData.c_showCalloutMessage || !promotionData.name)
            return null

        return (
            <Text {...styles.promotionText}>
                {promotionData?.name}
            </Text>
        )
    }

    const isGiftCard = product?.c_isGiftCard || product?.c_isEGiftCard
    const shouldShowFewItemsLeft = Boolean(
        isFewItemsLeft && product?.inventory?.orderable && !isGiftCard
    )
    const renderQuantityPicker = ({mb, my}) => {
        if (isGiftCard) return null

        return (
            <QuantityPicker
                opacity={isGreyedOut ? '0.5' : '1'}
                mb={mb}
                my={my}
                step={stepQuantity}
                value={quantity}
                min={1}
                max={product?.c_productMaxOrderQty}
                clampValueOnBlur={false}
                onBlur={(e) => {
                    // Default to last known quantity if a user leaves the box with an invalid value
                    const {value} = e.target
                    if (!value) {
                        setQuantity(product.quantity)
                    }
                }}
                onChange={(stringValue, numberValue) => {
                    // Set the quantity of product to value of input if value is a valid number
                    if (numberValue >= 0) {
                        onItemQuantityChange(numberValue).then(
                            (isValidChange) => isValidChange && setQuantity(numberValue)
                        )
                    } else if (stringValue === '') {
                        // Allow clearing the input before entering the next value
                        setQuantity(stringValue)
                    }
                }}
                productName={product?.name}
                showInventoryMessage={showInventoryMessage}
            />
        )
    }

    const renderPriceAndPromotion = ({isMini = false} = {}) => (
        <>
            {isMini ? (
                <Text fontSize="sm">
                    <DisplayPrice
                        isDisplayPricePerUnit={true}
                        quantity={quantity}
                        priceData={priceData}
                        currency={activeCurrency}
                        showQuantity={true}
                        currentPriceProps={{
                            fontWeight: 'normal'
                        }}
                        listPriceProps={{
                            fontWeight: 'normal'
                        }}
                    />
                </Text>
            ) : (
                <DisplayPrice
                    priceData={priceData}
                    currency={activeCurrency}
                    currentPriceProps={{
                        fontWeight: 'normal'
                    }}
                    listPriceProps={{
                        fontWeight: 'normal'
                    }}
                />
            )}

            {renderPromotion()}

            {product?.c_isFinalSale && enableFinalSaleLabel && (
                <Text {...styles.finalSaleText}>
                    <FormattedMessage
                        defaultMessage="Final Sale*"
                        id="display_price.label.final_sale"
                    />
                </Text>
            )}
        </>
    )

    return (
        <Box
            {...styles.container}
            data-testid={`sf-cart-item-${product.productId ? product.productId : product.id}`}
            ref={ref}
        >
            <ItemVariantProvider variant={product}>
                {showLoading && <LoadingSpinner />}
                {isMiniCart ? (
                    <Stack {...styles.miniStack} marginBottom={(variant === 'orderReview' || variant === 'orderConfirmation') ? (isLastItem ? 0 : 6) : undefined}>
                        {/* A few left label */}
                        {shouldShowFewItemsLeft && (
                            <FewItemsLeftMessage
                                message={<FormattedMessage id="cart_minicart.few.left" defaultMessage="A few left" />}
                                containerProps={{
                                    marginTop: 0,
                                    justifyContent: 'flex-start'
                                }}
                            />
                        )}
                        <Flex {...styles.miniRow}>
                            <CartItemVariantImage
                                {...styles.miniImage}
                                imageProps={isGreyedOut && {opacity: '0.5'}}
                                isGiftCard={isGiftCard}
                                isFewItemsLeft={isFewItemsLeft}
                            />
                            <Stack {...styles.miniMetaStack}>
                                <Stack
                                    {...styles.miniMetaContentStack}
                                    opacity={isGreyedOut ? '0.5' : '1'}
                                >
                                    <CartItemVariantName />
                                    <CartItemBrand brand={product.brand?.toLowerCase()} />
                                    <Box data-testid="mini-cart-price" {...styles.miniPriceBox}>
                                        {renderPriceAndPromotion({isMini: true})}
                                    </Box>
                                    <CartItemVariantAttributes opacity={isGreyedOut ? '0.5' : '1'} fontSize={variant === 'orderReview' ? {base: '12px', lg: '14px'} : undefined} />
                                </Stack>
                                {isGreyedOut && product?.inventory && (
                                    <Box {...styles.unavailableBox}>
                                        <Text {...styles.unavailableText}>
                                            <FormattedMessage
                                                defaultMessage="This item is no longer available and won't be added to your order."
                                                id="product_item.message.no_longer_available"
                                            />
                                        </Text>
                                    </Box>
                                )}
                                <Stack {...styles.miniActionsStack}>
                                    {wishlistAction}
                                    {removeAction}
                                </Stack>
                            </Stack>
                        </Flex>
                        <Box {...styles.mobilePrimaryActionBox}>
                            {primaryAction}
                        </Box>
                    </Stack>
                ) : (
                    <Stack {...styles.regularStack}>
                        {shouldShowFewItemsLeft && (
                            <FewItemsLeftMessage
                                containerProps={{
                                    marginTop: 0,
                                    justifyContent: 'flex-start'
                                }}
                            />
                        )}
                        <Flex {...styles.regularRow}>
                            <CartItemVariantImage
                                {...styles.regularImage}
                                imageProps={isGreyedOut && {opacity: '0.5'}}
                                isGiftCard={isGiftCard}
                                isFewItemsLeft={isFewItemsLeft}
                            />
                            <Flex {...styles.regularContent}>
                                <Stack {...styles.regularMetaStack} opacity={isGreyedOut ? '0.5' : '1'}>
                                    <CartItemVariantName />
                                    <CartItemBrand brand={product.brand?.toLowerCase()} />
                                    <Box data-testid="cart-price" {...styles.regularPriceBox}>{renderPriceAndPromotion()}</Box>
                                    <CartItemVariantAttributes
                                        quantity={quantity}
                                        opacity={isGreyedOut ? '0.5' : '1'}
                                    />
                                    <Box {...styles.regularQuantityBox}>
                                        {renderQuantityPicker({})}
                                    </Box>
                                    <VisuallyHidden role="status">
                                        {product?.name}
                                        {intl.formatMessage(
                                            {
                                                id: 'item_variant.assistive_msg.quantity',
                                                defaultMessage: 'Quantity {quantity}'
                                            },
                                            {
                                                quantity: product?.quantity
                                            }
                                        )}
                                    </VisuallyHidden>

                                    {isGreyedOut && product?.inventory && (
                                        <Box {...styles.unavailableBox}>
                                            <Text {...styles.unavailableText}>
                                                <FormattedMessage
                                                    defaultMessage="This item is no longer available and won't be added to your order."
                                                    id="product_item.message.no_longer_available"
                                                />
                                            </Text>
                                        </Box>
                                    )}

                                    {editAction}

                                    <Stack {...styles.mobileActionsStack}>
                                        {wishlistAction}
                                        {removeAction}
                                    </Stack>
                                </Stack>

                                <Stack {...styles.desktopActionsStack}>
                                    {wishlistAction}
                                    {removeAction}
                                </Stack>
                            </Flex>
                        </Flex>

                        <Box {...styles.mobilePrimaryActionBox}>
                            {primaryAction}
                        </Box>
                    </Stack>
                )}
            </ItemVariantProvider>
        </Box>
    )
}

export default forwardRef(ProductItem)

ProductItem.propTypes = {
    product: PropTypes.object,
    onItemQuantityChange: PropTypes.func,
    onAddItemToCart: PropTypes.func,
    showLoading: PropTypes.bool,
    isWishlistItem: PropTypes.bool,
    primaryAction: PropTypes.node,
    removeAction: PropTypes.node,
    editAction: PropTypes.node,
    wishlistAction: PropTypes.node,
    isMiniCart: PropTypes.bool,
    variant: PropTypes.string,
    isFewItemsLeft: PropTypes.bool,
    isLastItem: PropTypes.bool
}

